import React, { useEffect, useState, useCallback } from "react";
import {
	addToast,
	Autocomplete,
	AutocompleteItem,
	Avatar,
	Button,
	Card,
	CardBody,
	CardHeader,
	DateRangePicker,
	Divider,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	Select,
	SelectItem,
	Spinner,
	ToastProvider,
} from "@heroui/react";
import ActionTable from "../components/ActionTable";
import axios from "axios";
import { Search, RotateCcw } from "lucide-react";
import UserChip from "../components/UserChip";
import MActions from "../components/MActions";
import FILTER_DOC from "../assets/filter.md?raw";
import Markdown from "react-markdown";
import { NavLink } from "react-router-dom";
import MixToolTip from "../components/MixToolTip";

const AVATAR_API =
	(typeof window !== "undefined" && window.AVATAR_API) ||
	"https://workers.vrp.moe/bilibili/avatar/";

const filterItems = [
	{ key: "", value: "全部事件" },
	{ key: "1", value: "弹幕" },
	{ key: "2", value: "礼物" },
	{ key: "3", value: "大航海" },
	{ key: "4", value: "SuperChat" },
	{ key: "5", value: "切断" },
	{ key: "7", value: "警告" },
	{ key: "9", value: "红包" },
];

const orderOptions = [
	{ key: "time_desc", value: "时间倒序" },
	{ key: "gift_price_desc", value: "￥" },
	{ key: "time", value: "时间正序" },
];

function RawPage(props) {
	const [filter, setFilter] = useState("");
	const [room, setRoom] = useState("");
	const [input, setInput] = useState("");
	const [roomList, setRoomList] = useState([]);

	// 用户自动补全状态
	const [userInput, setUserInput] = useState("");
	const [userUid, setUserUid] = useState("");
	const [userList, setUserList] = useState([]);

	const [order, setOrder] = useState("time_desc");
	const [keyword, setKeyword] = useState("");
	const [dateRange, setDateRange] = useState(null);
	const [actionRes, setActionRes] = useState({});

	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [pageSize, setPageSize] = useState(
		parseInt(localStorage.getItem("defaultPageSize")) || 50,
	);

	// 主播搜索建议
	useEffect(() => {
		axios
			.get("/api/search?type=name&key=" + encodeURIComponent(input))
			.then((response) => {
				setRoomList(response.data.data ?? []);
			});
	}, [input]);

	// 用户搜索建议 (支持用户名模糊搜索 + 纯数字 UID 直接匹配)
	useEffect(() => {
		const trimmed = userInput.trim();
		const timer = setTimeout(() => {
			axios
				.get(
					"/api/search?type=watcher-name&key=" +
						encodeURIComponent(trimmed),
				)
				.then((response) => {
					let list = response.data?.data ?? [];
					// 如果输入的是纯数字 UID 且联想结果中没有，自动在最上方生成直接匹配项
					if (/^\d+$/.test(trimmed)) {
						const uidNum = parseInt(trimmed, 10);
						if (!list.some((u) => u.UID === uidNum)) {
							list = [
								{
									UID: uidNum,
									UName: `指定 UID: ${trimmed}`,
									MedalName: "",
									MedalLevel: 0,
								},
								...list,
							];
						}
					}
					setUserList(list);
				});
		}, 250);
		return () => clearTimeout(timer);
	}, [userInput]);

	// 统一的数据获取函数
	const fetchActions = useCallback(
		(targetPage = 1, customParams = {}) => {
			const currentRoom =
				customParams.room !== undefined ? customParams.room : room;
			const currentUid =
				customParams.uid !== undefined ? customParams.uid : userUid;
			const currentFilter =
				customParams.filter !== undefined
					? customParams.filter
					: filter;
			const currentOrder =
				customParams.order !== undefined ? customParams.order : order;
			const currentSize =
				customParams.pageSize !== undefined
					? customParams.pageSize
					: pageSize;
			const currentKeyword =
				customParams.keyword !== undefined
					? customParams.keyword
					: keyword;
			const currentDateRange =
				customParams.dateRange !== undefined
					? customParams.dateRange
					: dateRange;

			let url = `/api/actions?room=${currentRoom || ""}&type=${currentFilter || ""}&pn=${targetPage}&order=${currentOrder || "time_desc"}&ps=${currentSize}&pb=1`;
			if (currentUid) {
				url += `&uid=${encodeURIComponent(currentUid)}`;
			}
			if (currentKeyword && currentKeyword.trim()) {
				url += `&keyword=${encodeURIComponent(currentKeyword.trim())}`;
			}
			if (currentDateRange?.start && currentDateRange?.end) {
				url += `&begin=${currentDateRange.start.toString()}&end=${currentDateRange.end.toString()}`;
			}

			const hasUid = Boolean(currentUid && String(currentUid).trim());
			const hasRoom = Boolean(currentRoom && String(currentRoom).trim());
			let hasValidTimeRange = false;
			if (currentDateRange?.start && currentDateRange?.end) {
				const s = currentDateRange.start;
				const e = currentDateRange.end;
				const startDate = new Date(s.year, s.month - 1, s.day);
				const endDate = new Date(e.year, e.month - 1, e.day);
				if (startDate > endDate) {
					addToast({
						title: "提示",
						description: "起始时间不能晚于结束时间",
						hideIcon: true,
					});
					return;
				}
				const diffDays =
					(endDate.getTime() - startDate.getTime()) /
					(1000 * 60 * 100 * 24);
				if (diffDays <= 60) {
					hasValidTimeRange = true;
				}
			}
			var valid = true;
			if (keyword !== "" && !hasValidTimeRange && !hasUid && !hasRoom) {
				//关键词不为空，且没有其他条件或者60天内
				valid = false;
			}
			if (
				order !== "gift_price_desc" &&
				!hasUid &&
				!hasRoom &&
				!hasValidTimeRange
			) {
				//不按礼物价格排序
				valid = false;
			}

			if (
				keyword !== "" &&
				keyword.trim().length < 2 &&
				!hasUid &&
				!hasRoom
			) {
				valid = false;
			}

			if (currentFilter === "4") {
				valid = true;
			}

			if (!valid) {
				//setOpenModal(true)
				//return;
			}

			setLoading(true);
			axios
				.get(url)
				.then((response) => {
					console.log(response.data);
					if (
						response.data.length === 0 &&
						response.data.statistics.statistics.startsWith("5.")
					) {
						addToast({
							title: "Error",
							description: "查询超时，请尝试缩小条件范围",
							hideIcon: true,
							color: "secondary",
							timeout: 10000,
						});
						return;
					}
					console.log(response.data);
					setActionRes(response.data);
					setTotal(response.data?.total ?? 0);
				})
				.catch((err) => {
					addToast({
						title: "Error",
						description:
							err?.data?.message ||
							err?.message ||
							"获取数据失败",
						hideIcon: true,
					});
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[room, userUid, filter, order, pageSize, keyword, dateRange],
	);

	// 点击搜索按钮触发
	const handleSearch = () => {
		setPage(1);
		fetchActions(1);
	};

	// 重置所有筛选条件并重新拉取
	const handleReset = () => {
		setFilter("");
		setRoom("");
		setInput("");
		setUserUid("");
		setUserInput("");
		setUserList([]);
		setOrder("time_desc");
		setKeyword("");
		setDateRange(null);
		setPage(1);
		fetchActions(1, {
			room: "",
			uid: "",
			filter: "",
			order: "time_desc",
			keyword: "",
			dateRange: null,
		});
	};

	const [openModal, setOpenModal] = React.useState(false);

	return (
		<div>
			<Modal
				isOpen={openModal}
				onOpenChange={() => {
					setOpenModal(false);
				}}
			>
				<ModalContent>
					<ModalHeader className="flex flex-col gap-1">
						错误
					</ModalHeader>
					<ModalBody
						className={"markdown-body "}
						style={{
							listStyle: "!circle",
						}}
					>
						<Markdown>{FILTER_DOC}</Markdown>
					</ModalBody>
					<ModalFooter>
						<Button
							color="primary"
							onPress={() => {
								setOpenModal(false);
							}}
						>
							关闭
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
			<div className="mt-4">
				{/* 筛选卡片工具栏 */}
				<Card className="mb-4 bg-default-50/70 dark:bg-default-100/20 border border-default-200/50 shadow-sm rounded-2xl">
					<CardBody className="p-3.5 gap-3">
						{/* 第一行：主要检索区（主播、用户、关键词、时间范围、搜索与重置按钮） */}
						<div className="flex flex-wrap items-center gap-3">
							{/* 1. 直播间选择 (自动补全) */}
							<div className="w-full sm:w-52">
								<Autocomplete
									size="sm"
									label="直播间"
									placeholder="搜索主播"
									items={roomList}
									selectedKey={room}
									onSelectionChange={(key) =>
										setRoom(key ? String(key) : "")
									}
									onInputChange={(val) => setInput(val)}
									isClearable
									onClear={() => {
										setRoom("");
										setInput("");
									}}
								>
									{(f) => (
										<AutocompleteItem
											key={f.Room}
											textValue={f.UName}
										>
											<div className="flex flex-row items-center gap-2">
												<Avatar
													size="sm"
													src={`${AVATAR_API}${f.UID}`}
													className="w-6 h-6 flex-shrink-0"
												/>
												<span className="font-bold text-sm truncate">
													{f.UName}
												</span>
											</div>
										</AutocompleteItem>
									)}
								</Autocomplete>
							</div>

							<div className="w-full sm:w-56">
								<Autocomplete
									size="sm"
									label="用户"
									placeholder="搜索用户名/UID"
									isVirtualized={false}
									items={userList}
									selectedKey={userUid}
									onSelectionChange={(key) =>
										setUserUid(key ? String(key) : "")
									}
									onInputChange={(val) => setUserInput(val)}
									isClearable
									onClear={() => {
										setUserUid("");
										setUserInput("");
										setUserList([]);
									}}
								>
									{(u) => (
										<AutocompleteItem
											key={String(u.UID)}
											textValue={`${u.UName} (${u.UID})`}
										>
											<div className="flex flex-row items-center gap-2">
												<UserChip
													props={{
														FromName: u.UName,
														FromId: u.UID,
														MedalName: u.MedalName,
														MedalLevel:
															u.MedalLevel,
														GuardLevel: u.Type,
													}}
												></UserChip>
											</div>
										</AutocompleteItem>
									)}
								</Autocomplete>
							</div>

							<div className="w-full sm:w-48">
								<Input
									size="sm"
									label="关键词"
									placeholder=""
									value={keyword}
									onValueChange={setKeyword}
									isClearable
									onClear={() => setKeyword("")}
									startContent={
										<Search className="w-4 h-4 text-default-400 pointer-events-none flex-shrink-0" />
									}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											handleSearch();
										}
									}}
								/>
							</div>

							{/* 4. 时间范围 */}
							<div className="w-full sm:w-72">
								<DateRangePicker
									size="sm"
									label="时间范围"
									value={dateRange}
									onChange={setDateRange}
									className="w-full"
									visibleMonths={2}
								/>
							</div>

							{/* 5. 搜索与重置按钮 */}
							<div className="flex items-center gap-2">
								<Button
									color="primary"
									isLoading={loading}
									onPress={handleSearch}
									startContent={
										!loading && (
											<Search className="w-4 h-4" />
										)
									}
								>
									搜索
								</Button>
								<Button
									variant="flat"
									color="default"
									onPress={handleReset}
									startContent={
										<RotateCcw className="w-4 h-4" />
									}
								>
									重置
								</Button>
							</div>
						</div>

						{/* 分割线 */}
						<Divider className="my-0.5 opacity-60" />

						{/* 第二行：次级控制栏 (左侧分类/排序，右侧条数统计/分页大小) */}
						<div className="flex flex-wrap items-center justify-between gap-3 text-sm">
							{/* 左侧：分类与排序 */}
							<div className="flex flex-wrap items-center gap-2.5">
								<Select
									size="sm"
									label="筛选"
									className="w-36"
									selectedKeys={filter ? [filter] : [""]}
									onSelectionChange={(e) => {
										const k = e.currentKey || "";
										setFilter(k);
										setPage(1);
										fetchActions(1, { filter: k });
									}}
								>
									{filterItems.map((f) => (
										<SelectItem key={f.key}>
											{f.value}
										</SelectItem>
									))}
								</Select>

								<Select
									size="sm"
									label="排序方式"
									className="w-36"
									selectedKeys={[order]}
									onSelectionChange={(e) => {
										const k = e.currentKey || "time_desc";
										setOrder(k);
										setPage(1);
										fetchActions(1, { order: k });
									}}
								>
									{orderOptions.map((f) => (
										<SelectItem key={f.key}>
											{f.value}
										</SelectItem>
									))}
								</Select>
							</div>

							{/* 右侧：统计条数与每页显示 */}
							<div className="flex items-center gap-3 ml-auto text-xs text-default-500">
								<span>
									共{" "}
									<strong className="text-foreground font-semibold">
										{total?.toLocaleString() ?? 0}
									</strong>{" "}
									条记录
								</span>
								<Select
									size="sm"
									label="每页"
									className="w-28"
									selectedKeys={[String(pageSize)]}
									onSelectionChange={(e) => {
										const s = Number(e.currentKey);
										if (s) {
											setPageSize(s);
											localStorage.setItem(
												"defaultPageSize",
												s,
											);
											setPage(1);
											fetchActions(1, { pageSize: s });
										}
									}}
								>
									<SelectItem key="10">10</SelectItem>
									<SelectItem key="50">50</SelectItem>
									<SelectItem key="200">200</SelectItem>
									<SelectItem key="500">500</SelectItem>
								</Select>
							</div>
						</div>
					</CardBody>
				</Card>

				<div className="relative min-h-[200px]">
					{loading && (
						<div className="absolute inset-0 z-10 bg-white/70 dark:bg-[#18181b]/70 rounded-xl transition-opacity duration-150">
							<div className="sticky top-1/2 -translate-y-1/2 flex items-center justify-center">
								<Spinner label="加载中..." />
							</div>
						</div>
					)}
					<div
						className={`transition-opacity duration-300 ease-out ${
							loading ? "opacity-40" : "opacity-100"
						}`}
						style={{ willChange: "opacity" }}
					>
						{isMobile() ? (
							<div>
								{actionRes.items?.map((item) => {
									return (
										<RawLiveCard
											key={item.id}
											{...actionRes.lives?.filter(
												(x) => x.id === item.live,
											)[0]}
											actions={[item]}
										/>
									);
								})}
							</div>
						) : (
							<ActionTable
								dataSource={actionRes.items}
								handlePageChange={(page0) => {
									setPage(page0);
									fetchActions(page0);
									if (page0 >= 2) {
										window.USER_PAGE = page0;
									}
									window.scrollTo({
										top: 0,
										behavior: "smooth",
									});
								}}
								total={total}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default RawPage;

function MRawActions(props) {
	return <MActions item={props} />;
}

function RawLiveCard(item) {
	return (
		<Card className={`my-4 mx-2 '}`} isHoverable={isMobile()}>
			<CardHeader className="flex-col items-start">
				<NavLink to={`/lives/${item.id}`}>
					<p className="text-large uppercase ">{item.title}</p>
				</NavLink>

				<small className="text-default-500">
					{new Date(item.created_at).toLocaleString()}
				</small>
				<div className={" w-full flex flex-row"}>
					<div className={"w-[50%] flex-row flex items-center "}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 1024 1024"
							className=" text-black dark:text-white"
							fill="currentColor"
							style={{ width: "20px", height: "20px" }}
						>
							<path d="M464 512a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm200 0a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm-400 0a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm661.2-173.6c-22.6-53.7-55-101.9-96.3-143.3a444.35 444.35 0 0 0-143.3-96.3C630.6 75.7 572.2 64 512 64h-2c-60.6.3-119.3 12.3-174.5 35.9a445.35 445.35 0 0 0-142 96.5c-40.9 41.3-73 89.3-95.2 142.8-23 55.4-34.6 114.3-34.3 174.9A449.4 449.4 0 0 0 112 714v152a46 46 0 0 0 46 46h152.1A449.4 449.4 0 0 0 510 960h2.1c59.9 0 118-11.6 172.7-34.3a444.48 444.48 0 0 0 142.8-95.2c41.3-40.9 73.8-88.7 96.5-142 23.6-55.2 35.6-113.9 35.9-174.5.3-60.9-11.5-120-34.8-175.6zm-151.1 438C704 845.8 611 884 512 884h-1.7c-60.3-.3-120.2-15.3-173.1-43.5l-8.4-4.5H188V695.2l-4.5-8.4C155.3 633.9 140.3 574 140 513.7c-.4-99.7 37.7-193.3 107.6-263.8 69.8-70.5 163.1-109.5 262.8-109.9h1.7c50 0 98.5 9.7 144.2 28.9 44.6 18.7 84.6 45.6 119 80 34.3 34.3 61.3 74.4 80 119 19.4 46.2 29.1 95.2 28.9 145.8-.6 99.6-39.7 192.9-110.1 262.7z" />
						</svg>
						<h3 className={"mt-1 ml-0.5"}>{item.message}</h3>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 1024 1024"
							style={{ width: "20px", height: "20px" }}
							className="ml-1 text-black dark:text-white"
							fill="currentColor"
						>
							<path d="M911.5 700.7a8 8 0 0 0-10.3-4.8L840 718.2V180c0-37.6-30.4-68-68-68H252c-37.6 0-68 30.4-68 68v538.2l-61.3-22.3c-.9-.3-1.8-.5-2.7-.5-4.4 0-8 3.6-8 8V763c0 3.3 2.1 6.3 5.3 7.5L501 910.1c7.1 2.6 14.8 2.6 21.9 0l383.8-139.5c3.2-1.2 5.3-4.2 5.3-7.5v-59.6c0-1-.2-1.9-.5-2.8zM512 837.5l-256-93.1V184h512v560.4l-256 93.1zM660.6 312h-54.5c-3 0-5.8 1.7-7.1 4.4l-84.7 168.8H511l-84.7-168.8a8 8 0 0 0-7.1-4.4h-55.7c-1.3 0-2.6.3-3.8 1-3.9 2.1-5.3 7-3.2 10.8l103.9 191.6h-57c-4.4 0-8 3.6-8 8v27.1c0 4.4 3.6 8 8 8h76v39h-76c-4.4 0-8 3.6-8 8v27.1c0 4.4 3.6 8 8 8h76V704c0 4.4 3.6 8 8 8h49.9c4.4 0 8-3.6 8-8v-63.5h76.3c4.4 0 8-3.6 8-8v-27.1c0-4.4-3.6-8-8-8h-76.3v-39h76.3c4.4 0 8-3.6 8-8v-27.1c0-4.4-3.6-8-8-8H564l103.7-191.6c.6-1.2 1-2.5 1-3.8-.1-4.3-3.7-7.9-8.1-7.9z" />
						</svg>
						<span className=" mt-1 ml-0.5">
							{formatFloat(item.money)}
						</span>
					</div>
					<NavLink
						className={"w-[50%] flex justify-end"}
						to={"/liver/" + item.user_id}
					>
						<img
							className={"w-[40px] h-[40px] rounded-full"}
							src={`${AVATAR_API}${item.user_id}`}
						/>
						<span className="ml-1 my-1.5 text-default-500 cursor-pointer">
							{item.user_name}
						</span>
					</NavLink>
				</div>
			</CardHeader>
			<CardBody className="max-h-[400px] py-2">
				{item.actions.map((event) => {
					return <MActions item={event} hide_medal={true} />;
				})}
			</CardBody>
		</Card>
	);
}
